import { useEffect, useState, useCallback } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { Bell, CheckCheck, FileDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { useT } from "@/lib/i18n";
import {
  listMyManualNotifications,
  markManualNotificationRead,
  markAllManualNotificationsRead,
  type ManualNotificationRow,
} from "@/lib/manual-notifications.functions";

export function ManualNotificationsBell() {
  const { user } = useAuth();
  const { t, lang } = useT();
  const list = useServerFn(listMyManualNotifications);
  const markOne = useServerFn(markManualNotificationRead);
  const markAll = useServerFn(markAllManualNotificationsRead);
  const [rows, setRows] = useState<ManualNotificationRow[]>([]);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    try { setRows(await list()); } catch { /* silent */ }
  }, [list]);

  useEffect(() => {
    if (!user) return;
    refresh();
    const id = window.setInterval(refresh, 60_000);
    return () => window.clearInterval(id);
  }, [user, refresh]);

  if (!user) return null;

  const unread = rows.filter((r) => !r.read_at);
  const pick = (o: Record<string, string> | null | undefined) =>
    (o && (o[lang] || o.en || o.cs || Object.values(o)[0])) || "";

  const onOpen = async (n: ManualNotificationRow) => {
    if (!n.read_at) {
      try { await markOne({ data: { id: n.id } }); } catch { /* ignore */ }
      setRows((rs) => rs.map((r) => (r.id === n.id ? { ...r, read_at: new Date().toISOString() } : r)));
    }
    setOpen(false);
  };

  const onMarkAll = async () => {
    try { await markAll(); } catch { /* ignore */ }
    setRows((rs) => rs.map((r) => ({ ...r, read_at: r.read_at ?? new Date().toISOString() })));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t("manualNotif.aria") || "Notifikace manuálů"}
          className="relative inline-flex h-10 w-10 items-center justify-center rounded-md text-brand-earth hover:bg-brand-cream/60"
        >
          <Bell className="h-5 w-5" />
          {unread.length > 0 && (
            <span className="absolute -top-0.5 -right-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-terracotta px-1 text-[10px] font-bold text-white">
              {unread.length > 9 ? "9+" : unread.length}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <div className="text-sm font-semibold">{t("manualNotif.title") || "Nové manuály"}</div>
          {unread.length > 0 && (
            <Button size="sm" variant="ghost" onClick={onMarkAll} className="h-7 px-2 text-xs">
              <CheckCheck className="mr-1 h-3.5 w-3.5" />
              {t("manualNotif.markAll") || "Označit vše"}
            </Button>
          )}
        </div>
        <div className="max-h-80 overflow-auto">
          {rows.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground">
              {t("manualNotif.empty") || "Žádná oznámení."}
            </div>
          ) : (
            <ul className="divide-y">
              {rows.map((n) => (
                <li key={n.id} className={n.read_at ? "opacity-60" : ""}>
                  <Link
                    to="/ke-stazeni"
                    search={{ manual: n.slug || n.manual_id, lang, version: n.version }}
                    onClick={() => onOpen(n)}
                    className="block px-3 py-2 hover:bg-brand-cream/60"
                  >
                    <div className="flex items-start gap-2">
                      <FileDown className="mt-0.5 h-4 w-4 shrink-0 text-brand-terracotta" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate text-sm font-medium">{pick(n.title_i18n)}</span>
                          <Badge variant="outline" className="shrink-0 text-[10px]">v{n.version}</Badge>
                        </div>
                        {pick(n.description_i18n) && (
                          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                            {pick(n.description_i18n)}
                          </p>
                        )}
                        <div className="mt-1 flex items-center gap-2 text-[10px] text-muted-foreground">
                          <span>{new Date(n.created_at).toLocaleString()}</span>
                          {n.audience === "vakant_owners" && (
                            <Badge variant="secondary" className="text-[10px]">
                              {t("manualNotif.vakantOnly") || "Jen majitelé Vacantů"}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="border-t px-3 py-2 text-center">
          <Link to="/notifikace" onClick={() => setOpen(false)} className="text-xs font-medium text-brand-terracotta hover:underline">
            {t("manualNotif.viewAll") || "Zobrazit všechny notifikace"}
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
